import { FEATURE_FLAG_KEYS } from '@alpha-core/alpha-utils/lib/feature-flags';
import { Routes } from '@angular/router';
import { featureFlagGuard } from '@neutrinos/ngx-feature-flags';
import { environment } from '../environments/environment';
import { iconResolver, userResolver } from './app.resolver';
import { authGuard as projectsRedirect } from './guards/auth.guard';
import { branchValidationGuard } from './guards/branch-validation.guard';
import { branchGuard } from './guards/branch.guard';
import { isSavedGuard } from './guards/isSaved.guard';
import { userGuard } from './guards/user.guard';
import { pluginsReadyResolver } from './resolver/plugin.resolver';
import { projectResolver } from './resolver/project.resolver';
import { taskResolver } from './resolver/task.resolver';

export const routes: Routes = [
    {
        path: 'home',
        loadComponent: () =>
            import('./components/projects/landing-page/landing-page.component').then((m) => m.LandingPageComponent),
        canActivate: [userGuard, projectsRedirect],
        title: 'Create new project',
        resolve: {
            icons: iconResolver,
            user: userResolver,
        },
    },
    {
        path: 'project/:projectId',
        loadComponent: () =>
            import('./components/base-layout/base-layout.component').then((m) => m.BaseLayoutComponent),
        canActivate: [branchGuard, userGuard, branchValidationGuard],
        resolve: {
            project: projectResolver,
            icons: iconResolver,
            user: userResolver,
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'editors/model',
            },
            {
                path: 'editors/model',
                loadComponent: () =>
                    import('./components/case-definition/alpha-json-schema-editor/alpha-json-schema-editor.component').then(
                        (m) => m.AlphaJsonSchemaEditorComponent,
                    ),
                canDeactivate: [isSavedGuard],
            },
            {
                path: 'editors/tasks',
                loadComponent: () => import('./components/bpm/bpm.component').then((m) => m.BpmComponent),
            },
            {
                path: 'editors/pages',
                loadComponent: () =>
                    import('./components/pages/components/pages/pages.component').then((m) => m.PagesComponent),
                children: [
                    {
                        path: ':page',
                        loadComponent: () =>
                            import('./components/pages/components/page-editor/page-editor.component').then(
                                (m) => m.PageEditorComponent,
                            ),
                        data: {
                            currentComponent: 'globalPage',
                        },
                        resolve: {
                            plugins: pluginsReadyResolver,
                        },
                        canDeactivate: [isSavedGuard],
                    },
                ],
            },
            {
                path: 'editors/tasks/:task',
                loadComponent: () =>
                    import('./components/pages/components/pages/pages.component').then((m) => m.PagesComponent),
                canDeactivate: [isSavedGuard],
                data: {
                    currentComponent: 'tasks',
                },
                resolve: {
                    task: taskResolver,
                },
                children: [
                    {
                        path: ':page',
                        resolve: {
                            plugins: pluginsReadyResolver,
                        },
                        loadComponent: () =>
                            import('./components/pages/components/page-editor/page-editor.component').then(
                                (m) => m.PageEditorComponent,
                            ),
                    },
                ],
            },
            {
                path: 'editors/styles',
                loadComponent: () =>
                    import('./components/styles-menu/components/layout/layout.component').then(
                        (m) => m.StylesComponent,
                    ),
                canDeactivate: [isSavedGuard],
            },
            {
                path: 'editors/configs',
                resolve: {
                    plugins: pluginsReadyResolver,
                },
                loadComponent: () =>
                    import('./components/config-editor/config-editor.component').then((m) => m.ConfigEditorComponent),
                canDeactivate: [isSavedGuard],
            },
        ],
    },
    {
        path: 'workspace',
        loadComponent: () => import('./components/projects/workspace.component').then((m) => m.WorkspaceComponent),
        canActivate: [userGuard],
        resolve: {
            icons: iconResolver,
            user: userResolver,
        },
        children: [
            {
                path: 'apps',
                loadComponent: () =>
                    import('./components/projects/apps-list/apps-list.component').then((m) => m.AppsListComponent),
                title: 'Projects',
            },
            {
                path: 'custom-code',
                loadComponent: () =>
                    import('./components/custom-code/global-custom-code/global-custom-codes.component').then(
                        (m) => m.GlobalCustomCode,
                    ),
                title: 'Custom-code',
                canActivate: [featureFlagGuard],
                data: {
                    featureFlag: FEATURE_FLAG_KEYS.GLOBAL_CUSTOM_CODE,
                },
            },
            {
                path: 'custom-code/:configId/:versionId',
                loadComponent: () =>
                    import('./components/custom-code/custom-code-layout/custom-code-layout.component').then(
                        (m) => m.customCodeLayoutComponent,
                    ),
                title: 'Custom-code',
                canActivate: [featureFlagGuard],
                canDeactivate: [isSavedGuard],
                data: {
                    featureFlag: FEATURE_FLAG_KEYS.GLOBAL_CUSTOM_CODE,
                },
            },
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'apps',
            },
        ],
    },
    {
        path: '',
        redirectTo: '/home',
        resolve: {
            icons: iconResolver,
            user: userResolver,
        },
        pathMatch: 'full',
    },
    {
        path: 'unauthorized',
        loadComponent: () => import('@alpha-core/platform-ui').then((m) => m.UnauthorizedComponent),
        data: {
            backendUrl: environment.ALPHA_UI_AUTH_SERVICE_URL,
        },
    },
];
